import { useEffect, useMemo, useRef, useState } from 'react';
import { useContext } from 'react';
import type { UIElement } from '@adyen/adyen-web';
import type { AdyenGooglePayHandlers } from '@/types/adyen';
import { CheckoutContext } from '@/context/CheckoutContext';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import type { AdyenGooglePayConfig, AdyenGooglePayPaymentMethod } from '@/types/adyen';
import { adyenExpressService } from '@/services/adyenExpressService';
import type { AdyenFinalizePaymentResponse } from '@/types/adyen';
import type { ExpressCheckoutComponentProps } from '@/utils/expressPayment';
import { Errors } from '@/constants/errors';
import { logError, logInfo, logWarn } from '@/utils/logger';
import { adyenGooglePayAuthMethod } from '@/utils/googlePayAuthMethod';
import { captureRuntimeEnvironment, watchPageLifecycle } from '@/utils/paymentDiagnostics';
import { ErrorLogs, InfoLogs } from '@/constants/logs';
import { useAdyenExpressError } from '@/hooks/useAdyenExpressError';
import { useGooglePayShippingZipTax } from '@/hooks/useGooglePayShippingZipTax';
import { formatAdyenWalletAvailabilityReason, WALLET_AVAILABILITY_REASON } from '@/utils/adyenWalletAvailability';
import { notifyPaymentIntentClick } from '@/utils/paymentClickEvents';

// Google gives the page ~30s to answer a payment-data callback before the
// sheet dies with CALLBACK_TIMED_OUT. The watchdog fires just past that
// budget and reports which phase the session stopped at (see
// `InfoLogs.googlePaySheetStalled`).
const SHEET_WATCHDOG_MS = 35_000;

type SheetPhase = 'idle' | 'clicked' | 'authorized' | 'submitted';

export interface UseAdyenGooglePayOptions extends ExpressCheckoutComponentProps {
  onClick?: AdyenGooglePayHandlers['onClick'];
}

export function useAdyenGooglePay({ onReady, onClick }: UseAdyenGooglePayOptions = {}) {
  const context = useContext(CheckoutContext);
  const contextRef = useRef(context);
  contextRef.current = context;
  const {
    paymentMethodsSettings,
    pricing,
    customer,
    featureFlags,
    sessionStartedWithoutValidTax,
    actions: { runPayment },
  } = context;

  const [googlePayElement, setGooglePayElement] = useState<UIElement | null>(null);
  const [error, setErrorState] = useState<Error | null>(null);
  const isPaymentInProgress = useRef(false);
  const isPaySessionStartingRef = useRef(false);
  const submitParamsRef = useRef<{ email: string }>({ email: '' });
  // CALLBACK_TIMED_OUT diagnostics: track how far each sheet session got
  // (clicked → authorized → submitted) and arm a one-shot watchdog on click.
  // Log-only — never alters the payment flow.
  const sheetPhaseRef = useRef<SheetPhase>('idle');
  const sheetClickTsRef = useRef(0);
  const sheetWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Rejecting a blocked duplicate `onSubmit` makes the SDK call
  // `onPaymentFailed` (UIElement.handleFailedResult). That teardown belongs to
  // the FIRST submit, which is still awaiting `runPayment` and still owns the
  // SubmitActions stored on `adyenExpressService` — running it would clear
  // `isPaymentInProgress` / `isPaySessionStartingRef` underneath it and let the
  // next click overwrite those pending actions. Set synchronously before the
  // reject so the (microtask-later) `onPaymentFailed` skips exactly that one.
  const skipNextPaymentFailedRef = useRef(false);

  const clearSheetWatchdog = () => {
    if (sheetWatchdogRef.current) {
      clearTimeout(sheetWatchdogRef.current);
      sheetWatchdogRef.current = null;
    }
  };

  const armSheetWatchdog = () => {
    clearSheetWatchdog();
    sheetClickTsRef.current = Date.now();
    sheetPhaseRef.current = 'clicked';
    sheetWatchdogRef.current = setTimeout(() => {
      sheetWatchdogRef.current = null;
      const actionsPending = adyenExpressService.hasPendingGooglePayActions();
      // A completed session (submitted + actions settled) is healthy; anything
      // else past Google's budget is the CALLBACK_TIMED_OUT fingerprint.
      if (sheetPhaseRef.current === 'submitted' && !actionsPending) return;
      logWarn(
        InfoLogs.googlePaySheetStalled,
        { msSinceClick: Date.now() - sheetClickTsRef.current },
        {
          payment_method: PaymentMethodEnum.GOOGLE_PAY,
          payment_provider: ProviderEnum.ADYEN,
          sheet_phase: sheetPhaseRef.current,
          actions_pending: actionsPending,
        }
      );
    }, SHEET_WATCHDOG_MS);
  };

  // ACDEV-25067: recompute tax from the wallet ZIP and patch the open Google Pay
  // sheet total. Read through a ref so the (pricing-dependent) callback identity
  // never re-initializes the component mid-sheet — the effect below keys on
  // `pricing` only.
  const { onPaymentDataChanged, capturedWalletTaxRef, applyWalletTaxToCheckout } = useGooglePayShippingZipTax({
    provider: 'adyen',
  });
  const onPaymentDataChangedRef = useRef(onPaymentDataChanged);
  onPaymentDataChangedRef.current = onPaymentDataChanged;
  // Latest `applyWalletTaxToCheckout` behind a ref so the SDK handler closure
  // captured on mount always calls the current version — same pattern as
  // `onPaymentDataChangedRef`, and for the same reason: we don't want handler
  // identity to force a component re-init.
  const applyWalletTaxToCheckoutRef = useRef(applyWalletTaxToCheckout);
  applyWalletTaxToCheckoutRef.current = applyWalletTaxToCheckout;

  const googlePayPaymentMethod = useMemo(
    () =>
      paymentMethodsSettings.find(
        (pm) => pm.paymentMethodName === PaymentMethodEnum.GOOGLE_PAY && pm.provider === ProviderEnum.ADYEN
      ) as AdyenGooglePayPaymentMethod | undefined,
    [paymentMethodsSettings]
  );

  const { handleError, handleCancel } = useAdyenExpressError({
    paymentMethod: PaymentMethodEnum.GOOGLE_PAY,
    isPaymentInProgress,
    isPaySessionStartingRef,
    // Rides on every `expressPaymentFailed` log: how far the sheet session got
    // and how long ago the click was. Lets RUM split "user closed the sheet
    // after CALLBACK_TIMED_OUT" (phase=clicked, ms_since_click > 30s) from a
    // quick genuine cancel.
    getDiagnostics: () => ({
      sheet_phase: sheetPhaseRef.current,
      ms_since_click: sheetClickTsRef.current ? Date.now() - sheetClickTsRef.current : -1,
      actions_pending: adyenExpressService.hasPendingGooglePayActions(),
    }),
  });

  useEffect(() => {
    if (!googlePayPaymentMethod) {
      onReady?.(false, WALLET_AVAILABILITY_REASON.METHOD_NOT_OFFERED);
      return;
    }
    let googlePayInstance: UIElement | null = null;

    const initGooglePay = async () => {
      try {
        await adyenExpressService.initializeCheckoutInstance(
          googlePayPaymentMethod.publicConfig as AdyenGooglePayConfig,
          customer ?? undefined,
          googlePayPaymentMethod.paymentMethodConfig as Record<string, unknown> | undefined
        );

        const guardedOnClick: AdyenGooglePayHandlers['onClick'] = (resolve, reject) => {
          if (isPaySessionStartingRef.current) {
            // A previous `loadPaymentData` request is still pending. `resolve()`
            // issues a second one, which Google rejects with
            // `statusCode: CANCELED` — its client reference lists "The method
            // was called while the Google Pay payment sheet was already shown"
            // under that code. adyen-web turns that rejection into
            // `AdyenCheckoutError('CANCEL')` on `onError`, so the duplicate
            // click tears down the bookkeeping of the sheet that is STILL open.
            // `reject()` never reaches the sheet: `GooglePay.submit` runs
            // `new Promise(onClick).then(showGooglePayPaymentSheet).catch(() => {})`,
            // so rejecting only skips the second `loadPaymentData`. Nothing can
            // close an open Google Pay sheet from JS anyway (Adyen/adyen-web#3586).
            logError(ErrorLogs.googlePayDuplicateClick, undefined, undefined, {
              payment_method: PaymentMethodEnum.GOOGLE_PAY,
              payment_provider: ProviderEnum.ADYEN,
              sheet_phase: sheetPhaseRef.current,
              ms_since_click: Date.now() - sheetClickTsRef.current,
            });
            reject();
            return;
          }
          isPaySessionStartingRef.current = true;
          armSheetWatchdog();
          if (onClick) {
            onClick(resolve, reject);
          } else {
            resolve();
          }
        };

        // ACDEV-25067: pair the callback's presence with `useGooglePayShippingZipTax`'s
        // US/CA gate. When the hook returned `undefined` (non-US/CA session), we
        // hand Adyen NO `onPaymentDataChanged` at all — matching Stripe ECE's
        // `onShippingAddressChange: shouldCollectZipForTax ? ... : undefined`
        // pattern. Read the ref at effect-run time; the ref's identity is
        // stable, so this doesn't couple the effect to `customer` deps.
        const hasShippingCallback = !!onPaymentDataChangedRef.current;

        const handlers: AdyenGooglePayHandlers = {
          onClick: guardedOnClick,
          // ACDEV-25067: fires on SHIPPING_ADDRESS / SHIPPING_OPTION (and on
          // initialize) before authorization — the only pre-auth hook that
          // carries the buyer's ZIP. The stable wrapper defers to the latest
          // pricing-aware callback without re-creating the component.
          ...(hasShippingCallback
            ? {
                onPaymentDataChanged: (intermediatePaymentData) =>
                  onPaymentDataChangedRef.current!(intermediatePaymentData),
              }
            : {}),
          onAuthorized: (data, actions) => {
            // First proof the Google→page callback bridge delivered the Pay
            // press. Sessions with a click but no `googlePayAuthorized` are
            // the CALLBACK_TIMED_OUT cohort (broken bridge / OEM browsers).
            sheetPhaseRef.current = 'authorized';
            logInfo(InfoLogs.googlePayAuthorized, undefined, {
              payment_method: PaymentMethodEnum.GOOGLE_PAY,
              payment_provider: ProviderEnum.ADYEN,
              ms_since_click: Date.now() - sheetClickTsRef.current,
              gpay_auth_method: adyenGooglePayAuthMethod(data),
            });
            submitParamsRef.current.email = data?.authorizedEvent?.email ?? customer?.email ?? '';
            actions.resolve();
          },
          onError: (err) => {
            clearSheetWatchdog();
            applyWalletTaxToCheckoutRef.current();
            handleError(err);
            sheetPhaseRef.current = 'idle';
          },
          // ACDEV-24269: Adyen invokes `onPaymentFailed` after we call
          // `actions.reject()` (via `completeGooglePayPayment(false)`) on a
          // server-reported failure or a thrown `runPayment`. Both of those
          // paths already fire `notifyPaymentIntentFailed` via the central
          // `handlePaymentResults` / `runPayment.catch` in CheckoutContent.
          // Firing it again here would duplicate the FE event — just clean
          // up local state.
          onPaymentFailed: () => {
            // The duplicate-submit rejection below produces one of these while
            // the first submit still owns the session — swallow it untouched.
            if (skipNextPaymentFailedRef.current) {
              skipNextPaymentFailedRef.current = false;
              return;
            }
            clearSheetWatchdog();
            sheetPhaseRef.current = 'idle';
            applyWalletTaxToCheckoutRef.current();
            handleCancel();
          },
          onSubmit: async (state, _, actions) => {
            if (isPaymentInProgress.current) {
              // NEVER leave the SubmitActions unsettled: an unanswered
              // `onPaymentAuthorized` keeps the sheet spinning until Google
              // kills it with CALLBACK_TIMED_OUT (~30s). Reject the duplicate
              // — the same SDK path a server decline already takes
              // (`completeGooglePayPayment(false)`). That answers Google with
              // `transactionState: 'ERROR'`, so the sheet shows an error the
              // shopper can dismiss or retry from instead of hanging.
              // The first submit keeps `isPaymentInProgress`, the watchdog and
              // the SubmitActions it registered — this branch must not touch
              // any of them, hence the skipped `onPaymentFailed`.
              logError(ErrorLogs.googlePaySubmitBlocked, undefined, undefined, {
                payment_method: PaymentMethodEnum.GOOGLE_PAY,
                payment_provider: ProviderEnum.ADYEN,
                sheet_phase: sheetPhaseRef.current,
                ms_since_click: Date.now() - sheetClickTsRef.current,
              });
              skipNextPaymentFailedRef.current = true;
              actions.reject();
              return;
            }
            sheetPhaseRef.current = 'submitted';
            isPaymentInProgress.current = true;
            adyenExpressService.setGooglePayActions(actions);

            // ACDEV-26410: the finalize call runs while the native Google Pay
            // sheet owns the screen and this page is backgrounded behind it —
            // the window where 70 production sessions went dark without ever
            // reaching a 3DS challenge. Bracket it, and watch for the browser
            // announcing the page's end while it is in flight.
            const finalizeStartedAt = Date.now();
            logInfo(InfoLogs.googlePayFinalizeRequested, captureRuntimeEnvironment(), {
              payment_method: PaymentMethodEnum.GOOGLE_PAY,
              payment_provider: ProviderEnum.ADYEN,
              ms_since_click: finalizeStartedAt - sheetClickTsRef.current,
            });
            // `activeMethod` matches the field the challenge-side watcher stamps,
            // so one query reads both phases of the same log message.
            const stopLifecycleWatch = watchPageLifecycle(InfoLogs.paymentPageLifecycle, () => ({
              activeMethod: PaymentMethodEnum.GOOGLE_PAY,
              phaseContext: 'google_pay_finalize',
            }));

            try {
              notifyPaymentIntentClick(contextRef.current, { paymentMethodName: PaymentMethodEnum.GOOGLE_PAY });
              const finalizeResponse = await runPayment<AdyenFinalizePaymentResponse>(
                {
                  paymentMethod: state.data.paymentMethod,
                  browserInfo: state.data.browserInfo,
                  email: submitParamsRef.current.email || customer?.email,
                  zipCode: capturedWalletTaxRef.current?.zipCode,
                },
                PaymentMethodEnum.GOOGLE_PAY,
                ProviderEnum.ADYEN
              );
              // `requiresAction` is the fork that decides whether a 3DS
              // challenge should follow, so recording it here is what makes a
              // missing `adyen3dsChallengeMounted` attributable to the handoff
              // rather than to a payment that simply never needed a challenge.
              logInfo(InfoLogs.googlePayFinalizeReturned, {
                durationMs: Date.now() - finalizeStartedAt,
                actionType: finalizeResponse?.adyen?.action?.type ?? 'none',
                visibilityState: document.visibilityState,
              });
            } catch {
              // ACDEV-24269: `runPayment` swallows its own errors and fires
              // `notifyPaymentIntentFailed` from its own catch, so we must
              // not fire it again here. Just reject so the Google Pay sheet
              // closes if we ever land here.
              applyWalletTaxToCheckoutRef.current();
              adyenExpressService.completeGooglePayPayment(false);
            } finally {
              stopLifecycleWatch();
              isPaymentInProgress.current = false;
              isPaySessionStartingRef.current = false;
            }
          },
        };

        const component = await adyenExpressService.createGooglePayComponent({
          googlePayPaymentMethod,
          customer,
          pricing,
          handlers,
          featureFlags,
          sessionStartedWithoutValidTax,
        });

        if (!component) return;

        googlePayInstance = component;
        setGooglePayElement(googlePayInstance);
        await component.isAvailable();
        onReady?.(true);
      } catch (err) {
        logError('Adyen Google Pay initialization error', err);
        setErrorState(err instanceof Error ? err : new Error(Errors.payment.failedToInitializeGooglePay));
        onReady?.(false, formatAdyenWalletAvailabilityReason(err));
      }
    };

    initGooglePay();

    return () => {
      clearSheetWatchdog();
      googlePayInstance?.unmount();
    };
  }, [pricing]);

  return {
    googlePayElement,
    error,
  };
}
